import React from 'react';
import sanitize from 'sanitize-html';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image from 'frontastic/lib/image';
import { NationalBook } from 'fonts';
import type { Cta } from '../generic-section/types';

/** Section/title/description alignment helper — never hardcode center. */
export function textAlignmentClass(alignment?: 'Left' | 'Right' | 'Center'): string {
  return alignment === 'Left' ? 'text-left' : alignment === 'Right' ? 'text-right' : 'text-center';
}

/**
 * Sanitize CMS-supplied HTML, preserving `target` so external links keep target="_blank".
 *
 * Uses pure-JS `sanitize-html`, NOT `dompurify`. `import { sanitize } from 'dompurify'` resolves to
 * `undefined` on the server (dompurify needs a DOM and reports isSupported:false), so calling it
 * during SSR threw `sanitize is not a function`. React abandoned the HTML pass for the whole page
 * and shipped `<body></body>` with the content only in the RSC payload -- meaning every page using
 * a V2 rendering server-rendered nothing at all. Same class of failure as the jsdom/Netlify
 * incident recorded in the repo's CLAUDE.md; same fix.
 */
export function sanitizeHtml(html?: string): string {
  return sanitize(html ?? '', SANITIZE_OPTIONS);
}

/**
 * CMS rich text is authored in Contentstack by staff, so this is defence in depth rather than the
 * only line of defence: allow the formatting tags the editor produces, keep `target`/`style` on
 * links and media, and drop script/style/event handlers.
 */
export const SANITIZE_OPTIONS: sanitize.IOptions = {
  allowedTags: sanitize.defaults.allowedTags.concat(['img', 'h1', 'h2', 'iframe', 'figure', 'figcaption', 'span', 'u']),
  allowedAttributes: {
    '*': ['style', 'class', 'id', 'align', 'dir', 'title'],
    a: ['href', 'name', 'target', 'rel'],
    img: ['src', 'srcset', 'alt', 'title', 'width', 'height', 'loading'],
    iframe: ['src', 'width', 'height', 'allow', 'allowfullscreen', 'frameborder', 'title'],
  },
  allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  allowedIframeHostnames: ['www.youtube.com', 'youtube.com', 'player.vimeo.com'],
};

/**
 * Methven-styled CTA buttons. First CTA renders as the light/bordered button,
 * subsequent CTAs as the dark filled button (mirrors Methven's tiles-multiple-cta).
 */
/**
 * `fullWidthUntil` is the breakpoint at which buttons stop filling their column and shrink to their
 * label. 'md' (768px) is where they have always switched, which puts an iPad on the desktop side of
 * the line -- the live site keeps a full-width, centre-aligned button there. Sections that follow
 * the live layout pass 'lg'.
 *
 * Both variants are written out in full because Tailwind only generates classes it can see.
 */
/**
 * The vertical rhythm BETWEEN sections, carried by every rendering's outer wrapper.
 *
 * Margin, not padding, and on the sections rather than the container: a section's padding belongs to
 * the section (it sits inside its background), while the space between two of them belongs to
 * neither. Renderings with a background colour -- Nova, Nexus -- made that visible, since padding
 * used as separation simply extended the coloured block.
 *
 * Sections are flex items, so these margins do not collapse: two neighbours give 48px at the
 * smallest size and 84px at the largest. The container therefore carries no row gap of its own,
 * which would stack on top.
 */
export const SECTION_SPACING =
  'mt-24 mb-24 md:mt-30 md:mb-30 lg:mt-36 lg:mb-36 xl:mt-36 xl:mb-36 2xl:mt-42 2xl:mb-42';

export function SectionCtas({
  ctas,
  className = '',
  fullWidthUntil = 'md',
}: {
  ctas?: Cta[];
  className?: string;
  fullWidthUntil?: 'md' | 'lg';
}) {
  if (!ctas?.length) return null;
  const wide = fullWidthUntil === 'lg';
  const row = wide ? 'flex flex-col gap-10 lg:flex-row' : 'flex flex-col gap-10 md:flex-row';
  // A full-width button centres its label on its own: <button> is text-align:center by default.
  const shape = wide ? 'lg:w-fit lg:px-32 lg:py-18' : 'md:w-fit md:px-32 md:py-18';
  return (
    <div className={`${row} ${className}`}>
      {ctas.map((cta, index) => (
        <Link link={cta?.url ?? '#'} key={index} target={cta?.target || '_self'} rel="noopener noreferrer">
          <Button
            icon={cta?.icon?.url ? <Image src={cta.icon.url} alt={cta?.icon?.description ?? ''} width={18} height={18} /> : undefined}
            className={`border-1 z-10 w-full rounded-none border border-[#2B2C39] px-18 py-10 shadow-sm ${shape} ${
              index === 0
                ? 'bg-white hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4]'
                : 'bg-[#2B2C39] hover:!bg-[#394A54]'
            }`}
          >
            <Typography as="span" className={`text-16 xl:text-14 2xl:text-16 ${index === 0 ? 'text-[#090909]' : 'text-white'} ${NationalBook.className}`}>
              {cta?.label}
            </Typography>
          </Button>
        </Link>
      ))}
    </div>
  );
}
